import React, { useState, useContext, useRef, useEffect } from 'react';
import throttle from 'lodash/throttle';

import '../style/tinyslider.css';
import '../style/tailwind.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Header from '../components/Header';
import Global from '../style/global';
import ModalContext from '../store/modalContext';
import Modals from '../modals';
import { layoutTypes } from '../types/propTypes';

const Layout = ({ children, location }) => {
  const { open } = useContext(ModalContext);
  const [isScrolled, setIsScrolled] = useState(false);

  const navRef = useRef();
  navRef.current = isScrolled;

  const [showScrollTop, setShowScrollTop] = useState(false);

  // Custom cursor refs
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const cursorPos = useRef({ x: -100, y: -100 });
  const ringPos = useRef({ x: -100, y: -100 });

  useEffect(() => {
    const handleScroll = throttle(() => {
      const y = window.scrollY;
      const show = y > 70;
      if (navRef.current !== show) {
        setIsScrolled(show);
      }
      setShowScrollTop(y > 400);
    }, 100);

    document.addEventListener('scroll', handleScroll);
    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Custom cursor effect
  useEffect(() => {
    // Skip on touch devices
    if (
      typeof window !== 'undefined' &&
      window.matchMedia('(hover: none)').matches
    )
      return;

    const dot = dotRef.current;
    const ring = ringRef.current;
    if (!dot || !ring) return;

    const onMouseMove = e => {
      cursorPos.current = { x: e.clientX, y: e.clientY };
      dot.style.left = e.clientX + 'px';
      dot.style.top = e.clientY + 'px';
    };

    // Smooth ring follow with RAF
    let rafId;
    const followRing = () => {
      const dx = cursorPos.current.x - ringPos.current.x;
      const dy = cursorPos.current.y - ringPos.current.y;
      ringPos.current.x += dx * 0.15;
      ringPos.current.y += dy * 0.15;
      ring.style.left = ringPos.current.x + 'px';
      ring.style.top = ringPos.current.y + 'px';
      rafId = requestAnimationFrame(followRing);
    };
    rafId = requestAnimationFrame(followRing);

    const onMouseEnter = () => {
      dot.style.opacity = '1';
      ring.style.opacity = '1';
    };
    const onMouseLeave = () => {
      dot.style.opacity = '0';
      ring.style.opacity = '0';
    };

    // Hover detection for interactive elements
    const onOverInteractive = e => {
      const tag = e.target.tagName;
      if (
        e.target.closest('a, button, [role="button"], .slick-arrow') ||
        tag === 'A' ||
        tag === 'BUTTON'
      ) {
        document.body.classList.add('cursor-hover');
        document.body.classList.remove('cursor-text');
      } else if (tag === 'INPUT' || tag === 'TEXTAREA') {
        document.body.classList.add('cursor-text');
        document.body.classList.remove('cursor-hover');
      } else {
        document.body.classList.remove('cursor-hover', 'cursor-text');
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseover', onOverInteractive);
    document.addEventListener('mouseenter', onMouseEnter);
    document.addEventListener('mouseleave', onMouseLeave);

    return () => {
      cancelAnimationFrame(rafId);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseover', onOverInteractive);
      document.removeEventListener('mouseenter', onMouseEnter);
      document.removeEventListener('mouseleave', onMouseLeave);
      document.body.classList.remove('cursor-hover', 'cursor-text');
    };
  }, []);

  function renderModal() {
    if (!open) return null;
    const ModalComponent = Modals[open];
    return <ModalComponent />;
  }

  return (
    <>
      <Global />
      <div ref={dotRef} className="cursor-dot" />
      <div ref={ringRef} className="cursor-ring" />
      <Header scrolled={isScrolled} location={location} />
      {children}
      {renderModal()}

      {/* Scroll to top */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Scroll to top"
        style={{
          position: 'fixed',
          bottom: '32px',
          right: '32px',
          width: '48px',
          height: '48px',
          background: '#F08020',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 9999,
          opacity: showScrollTop ? 1 : 0,
          pointerEvents: showScrollTop ? 'auto' : 'none',
          transform: showScrollTop ? 'translateY(0)' : 'translateY(16px)',
          transition:
            'opacity 0.35s ease, transform 0.35s ease, background 0.3s ease',
        }}
        onMouseEnter={e => (e.currentTarget.style.background = '#C95E0F')}
        onMouseLeave={e => (e.currentTarget.style.background = '#F08020')}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#fff"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 19V5M5 12l7-7 7 7" />
        </svg>
      </button>
    </>
  );
};

Layout.propTypes = layoutTypes;

export default Layout;
